import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import {
    ApiService,
    BrandingModeService,
    FeatureService,
    IdentityPreferencesService,
    LoggerService,
} from '@netfoundry-ui/shared/services';
import { AuthorizationService } from '@netfoundry-ui/shared/authorization';
import { Subscription } from 'rxjs';
import { cloneDeep, isEmpty } from 'lodash';
import {NETWORK_SERVICE, NetworkServiceV2} from "@netfoundry-ui/shared/apiv2";

const DEFAULT_NETWORK_ID_KEY = 'default_network_id';
const DEFAULT_APP_ID_KEY = 'default_app_id';
const DEFAULT_NETWORK_ITEM = {
  name: 'Select a Network'
};

@Component({
    selector: 'app-user-settings',
    templateUrl: './user-settings.component.html',
    styleUrls: ['./user-settings.component.scss'],
    standalone: false
})
export class UserSettingsComponent implements OnInit, OnDestroy {
    company = '';
    domain = '';
    subscription = new Subscription();
    tenantLabel = '';
    userPreferences: any = {};
    orgPreferences: any = {};
    isLoading = false;
    defaultNetwork: any = DEFAULT_NETWORK_ITEM;
    defaultNetworkId = 'default';
    networks = [];
    appOptions = [
      {
        id: 'networks',
        name: 'Network',
        icon: 'icon-networks',
        hidden: !this.authorizationService.canListNetworks()
      },
      {
        id: 'organization',
        name: 'Organization',
        icon: 'icon-ManageOrg',
        hidden: !this.authorizationService.canListUserIdentities()
      },
            {
        id: 'frontdoor',
        name: 'Frontdoor',
        icon: 'icon-front-door',
        hidden: !this.authorizationService.canListFrontdoors()
      },
      {
        id: 'billing',
        name: 'Billing',
        icon: 'icon-ManageBilling',
        hidden: !this.authorizationService.canViewBillingSubscriptions() && !this.authorizationService.canViewEnterpriseBillingSubscriptions()
      }
    ];

    defaultAppId: any = this.appOptions[0].id;
    darkModeSetting = 'auto';

    // Theme picker (cloud-ziti). Values match darkModeSetting: auto / false / true.
    themeOptions = [
        { value: 'auto', label: 'System' },
        { value: 'false', label: 'Light' },
        { value: 'true', label: 'Dark' },
    ];
    themeMenuOpen = false;

    constructor(
        public brandingService: BrandingModeService,
        public growlerService: GrowlerService,
        public featureService: FeatureService,
        public identityPreferences: IdentityPreferencesService,
        private apiService: ApiService,
        public authorizationService: AuthorizationService,
        private logger: LoggerService,
        @Inject(NETWORK_SERVICE) public networkService: NetworkServiceV2
    ) {}

    ngOnInit() {
        this.isLoading = true;
        const html = document.getElementsByTagName('body')[0];

        this.brandingService.fetchBrandCss().then((rawCss) => {
            html.style.cssText = rawCss;
        });
        this.subscription.add(
            this.apiService.currentTenant.subscribe((tenant) => {
                this.tenantLabel = tenant.label;
            })
        );
        this.apiService.currentUserPreferences.subscribe((userPreferences) => {
            this.userPreferences = cloneDeep(userPreferences?.preferences);
            if (!this.userPreferences) {
                this.userPreferences = {};
            }
            this.checkDarkModeSetting();
            this.isLoading = false;
        });
        this.subscription.add(
            this.networkService._networks.subscribe((networks) => {
                this.networks = cloneDeep(networks);
                this.findDefaultNetwork();
            })
        );
        this.initPreferences();
        this.initNetworks();
    }

    initNetworks() {
      this.networkService.getNetworks({ pageParams: { size: 2000, page: 0 } }).then((networks: any) => {
        this.networks = cloneDeep(networks);
        this.findDefaultNetwork();
      });
    }

    initPreferences() {
      this.defaultAppId = this.identityPreferences.getPreference(DEFAULT_APP_ID_KEY);
    }

    findDefaultNetwork() {
      this.defaultNetworkId = this.identityPreferences.getPreference('default_network_id');
      this.networks?.forEach((network) => {
        if (this.defaultNetworkId === network.id) {
          this.defaultNetwork = network;
        }
      });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    toggleDarkMode() {
        this.brandingService.darkModeEnabled = !this.brandingService.darkModeEnabled;
        this.brandingService.updateDarkMode();
    }

    togglePowerUserSetting() {
        if (!this.userPreferences) {
            this.userPreferences = {};
        }
        this.userPreferences.powerUserEnabled = !this.userPreferences?.powerUserEnabled;
        this.featureService.setPowerUserEnabled(this.userPreferences.powerUserEnabled);
    }

    toggleSideNavigationSetting() {
        if (!this.userPreferences) {
            this.userPreferences = {};
        }
        this.userPreferences.enableNewNavigation = !this.userPreferences?.enableNewNavigation;
        this.featureService.enableNewNavigation = this.userPreferences.enableNewNavigation;
    }

    toggleManagedEntitiesSetting() {
      this.featureService.showManagedEntities = !this.featureService.showManagedEntities;
    }

    toggleBetaFeaturesSetting() {
      this.featureService.betaFeaturesEnabled = !this.featureService.betaFeaturesEnabled;
    }

    toggleZitiOverlaySetting() {
      this.featureService.zitiOverlayEnabled = !this.featureService.zitiOverlayEnabled;
    }

    copyToClipboard() {
        const setlocalStorage = Object.entries(localStorage);
        const bearerJwtObjectArray = setlocalStorage.find(([key]) => key.startsWith('@@auth0spajs@@') && !key.includes('@@user@@'));
        const bearerJwtObject = JSON.parse(bearerJwtObjectArray[1]);
        const bearerJwt = bearerJwtObject.body.access_token;
        navigator.clipboard.writeText(bearerJwt);
        this.growlerService.show(
            new GrowlerData('success', 'Success', 'Copied to clipboard', 'Jwt token has been copied to your clipboard')
        );
    }

    setDefaultNetwork(event) {
      if (this.defaultNetwork?.id === 'clear-default-network') {
        this.identityPreferences.setPreference(DEFAULT_NETWORK_ID_KEY, undefined);
        this.defaultNetwork = DEFAULT_NETWORK_ITEM;
        this.defaultNetworkId = 'default';
      } else {
        this.identityPreferences.setPreference(DEFAULT_NETWORK_ID_KEY, this.defaultNetwork?.id);
      }
    }

    setDefaultApp(event) {
      this.identityPreferences.setPreference(DEFAULT_APP_ID_KEY, this.defaultAppId);
    }

    setDarkMode(event) {
        this.featureService.darkModeEnabled = this.darkModeSetting;
        this.brandingService.updateDarkMode();
    }

    get selectedThemeOption() {
        return this.themeOptions.find((o) => o.value === this.darkModeSetting) ?? this.themeOptions[0];
    }

    toggleThemeMenu() {
        this.themeMenuOpen = !this.themeMenuOpen;
    }

    closeThemeMenu() {
        this.themeMenuOpen = false;
    }

    selectTheme(value: string) {
        this.darkModeSetting = value;
        this.themeMenuOpen = false;
        this.setDarkMode(value);
    }

    appMenuOpen = false;
    networkMenuOpen = false;

    toggleNetworkMenu() {
        this.networkMenuOpen = !this.networkMenuOpen;
    }

    closeNetworkMenu() {
        this.networkMenuOpen = false;
    }

    // Status tone for the selected default network, matching the picker's list-item dots.
    get defaultNetworkDotClass(): string {
        const s = (this.defaultNetwork?.status || '').toString().toUpperCase();
        if (s === 'PROVISIONED') return 'ok';
        if (s === 'ERROR') return 'crit';
        if (s === 'DELETING' || s === 'DELETED' || s === 'SUSPENDED') return 'muted';
        return 'warn';
    }

    onDefaultNetworkPicked(network: any) {
        this.networkMenuOpen = false;
        if (!network) {
            this.identityPreferences.setPreference(DEFAULT_NETWORK_ID_KEY, undefined);
            this.defaultNetwork = DEFAULT_NETWORK_ITEM;
            this.defaultNetworkId = 'default';
        } else {
            this.defaultNetwork = network;
            this.defaultNetworkId = network.id;
            this.identityPreferences.setPreference(DEFAULT_NETWORK_ID_KEY, network.id);
        }
    }

    get visibleAppOptions() {
        return this.appOptions.filter((o) => !o.hidden);
    }

    get selectedAppOption() {
        return this.appOptions.find((o) => o.id === this.defaultAppId);
    }

    toggleAppMenu() {
        this.appMenuOpen = !this.appMenuOpen;
    }

    closeAppMenu() {
        this.appMenuOpen = false;
    }

    selectApp(id: string) {
        this.defaultAppId = id;
        this.appMenuOpen = false;
        this.setDefaultApp(id);
    }

    checkDarkModeSetting() {
        const localStorageSetting = this.identityPreferences.getPreference('darkModeEnabled');
        let setting = localStorageSetting?.toString();
        if (isEmpty(setting)) {
          setting = 'auto';
        }
        this.darkModeSetting = setting.toString();
    }
}
